/**
 * Pure presentation of the save-status line: one polite live region with the
 * text and, for a failed or conflicting save, one inline link action.
 * @module features/scouting/components/molecules/PitchSaveStatusLine
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import type { WatchlistPreferenceStatus } from "@/features/scouting/hooks/watchlistPreferenceOrderState";

import { type PitchSaveSurface, TEST_ID_PITCH_SAVE } from "e2e/testId";

/** The inline action a status can offer. */
export type PitchSaveStatusAction = "retry" | "reload";

/** Props for {@link PitchSaveStatusLineView}. */
export interface PitchSaveStatusLineViewProps {
  readonly text: string;
  readonly status: WatchlistPreferenceStatus;
  readonly action?: PitchSaveStatusAction;
  readonly onAction?: () => void;
  /** Which host mounts the line; selects its test identity. */
  readonly surface?: PitchSaveSurface;
}

/** Semantic text tone per status. */
const TEXT_TONE: Readonly<Record<WatchlistPreferenceStatus, string>> = {
  idle: "text-content-muted",
  saving: "text-content-muted",
  saved: "text-content-muted",
  error: "text-status-error",
  conflict: "text-status-warning",
};

/** Visible label per inline action. */
const ACTION_LABEL: Readonly<Record<PitchSaveStatusAction, string>> = {
  retry: "Retry",
  reload: "Reload",
};

/**
 * Renders the status line.
 * @param root0 - Prepared line
 * @param root0.text - The status text
 * @param root0.status - Save status, for the text tone
 * @param root0.action - Inline action to offer, if any
 * @param root0.onAction - Handler for the inline action
 * @param root0.surface - Host, for the test identity (Pitch by default)
 * @returns The rendered line
 */
const PitchSaveStatusLineView = ({
  text,
  status,
  action,
  onAction,
  surface = "pitch",
}: PitchSaveStatusLineViewProps) => (
  <Box
    testID={TEST_ID_PITCH_SAVE[surface].status.ROOT}
    accessibilityLiveRegion="polite"
    className="flex flex-row flex-wrap items-center gap-1"
  >
    <Text
      testID={TEST_ID_PITCH_SAVE[surface].status.TEXT}
      variant="micro"
      className={TEXT_TONE[status]}
    >
      {text}
    </Text>
    {action && onAction ? (
      <>
        <Text variant="micro" className={TEXT_TONE[status]}>
          ·
        </Text>
        <Button
          testID={TEST_ID_PITCH_SAVE[surface].status.ACTION}
          label={ACTION_LABEL[action]}
          variant="link"
          size="xs"
          shape="rounded"
          labelVariant="micro-strong"
          labelTone={status === "error" ? "danger" : "warning"}
          className="h-auto min-h-0 px-0 py-0"
          onPress={onAction}
        />
      </>
    ) : null}
  </Box>
);

PitchSaveStatusLineView.displayName = "PitchSaveStatusLineView";
export default memo(PitchSaveStatusLineView);
